﻿import React, { useState, useEffect } from 'react';
import { GasChromatographyBenchSheet } from '../models/gas-chromatography-bench-sheet';
import { BenchSheetTechnologyType, DocumentType, Entities, NoteType, ModalMessageType, AnalysisScopeType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import HplcImpuritiesDataComponent from './HplcImpuritiesDataComponent';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetToolbar from './BenchSheetToolbar';
import AnalysisDataData from './AnalysisDataData';
import NoteViewer from './NoteViewer';
import GasChromatographyInstrument from './GasChromatographyInstrument';
import TemperatureProgramDataComponent from './TemperatureProgramDataComponent';
import BenchSheetColumnInformation from './BenchSheetColumnInformation';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetData from './BenchSheetData';
import { CrudFunctions } from '../utilities/crud-functions';
import StandardsViewer from './StandardsViewer';
import DocumentLoader from './DocumentLoader';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import MinimumSampleSizeComponent from './MinimumSampleSizeComponent';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import TicketLogEntryForm from './TicketLogEntryForm';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import DmaFooter from './DmaFooter';

import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export interface Props {
    handleError?: (message: string) => void;
}

export default function GasChromatographyBenchSheetForm(props: Props) {
    const [benchSheet, setBenchSheet] =
        useState<GasChromatographyBenchSheet>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [issuesFound, setIssuesFound] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getAllData();
    }, []);

    const parts = window.location.href.split('/');

    let isRawMaterial = false;

    if (parts[parts.length - 1] === "rmb") {
        isRawMaterial = true;
    }

    const benchSheetId = isRawMaterial ? parts[parts.length - 2] : parts[parts.length - 1];

    console.log("parts");
    console.log(parts);
    console.log("isRawMaterial:");
    console.log(isRawMaterial);

    key++;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    //TODO: this needs to be reworked - to look at Scopes:
    // const includesImpurities = benchSheet && benchSheet.analysisTypes ?
    //   benchSheet.analysisTypes.some(a => a.id === BenchSheetAnalysisType.Impurity) : false;

    const batchId = benchSheet && benchSheet.batchId ? benchSheet.batchId : undefined;

    return (
        <div className="pw-content">
            <BenchSheetToolbar title="GC Bench Sheet"
                subtitle="Gas Chromatography"
                benchSheetId={benchSheetId}
                showPrintButton={true}
                auditRowPointer={benchSheetId} entity={Entities.GcBenchSheets}
                technologyId={BenchSheetTechnologyType.GasChromatography}
                documentationId={DocumentationConstants.GCBenchSheet}
                showItemInformation={showBenchSheetInformationDialog}
                showTicketLog={showTicketLogEntryDialog} />

            <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

            <GasChromatographyInstrument readOnly={!canEdit}
                technology={BenchSheetTechnologyType.GasChromatography}
                benchSheetId={benchSheetId ? benchSheetId : undefined} />

            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Diluents</div>

            <div className="pw-margin-bottom-05">
                <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={true} />
            </div>

            <TemperatureProgramDataComponent benchSheetId={benchSheetId} datagridHeight="12rem"
                isReadOnly={!canEdit} />

            <div className="pw-comp-row-scroll-fixed-grid">
                <BenchSheetColumnInformation benchSheetId={benchSheetId} readOnly={!canEdit}
                    technology={BenchSheetTechnologyType.GasChromatography} />
            </div>

            {(benchSheet && benchSheet.scopeTypes && benchSheet.scopeTypes.includes(AnalysisScopeType.Homogeneity)) &&
                <MinimumSampleSizeComponent benchSheetId={benchSheetId} />
            }

            <div className="lq-sub-title pw-margin-left-05 pw-margin-top-05 pw-margin-bottom-05">Sample Weights</div>
            <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />

            <div className="pw-comp-row-scroll-fixed-grid">
                <AnalysisDataData
                    benchSheetId={benchSheetId ? benchSheetId : undefined}
                    batchId={benchSheet && benchSheet.batchId ?
                        benchSheet.batchId : undefined}
                    isReadOnly={!canEdit}
                    datagridHeight='16rem' />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <HplcImpuritiesDataComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    datagridHeight='16rem' />
            </div>

            <div className="pw-comp-row-scroll-fixed-grid">
                <BenchSheetData parentId={benchSheetId}
                    readOnly={!canEdit} canDelete={canDelete}
                    technology={BenchSheetTechnologyType.GasChromatography}
                    documentType={DocumentType.General}
                    imageHandlerTitle="C of A Images" />
            </div>

            {/*<div className="pw-margin-top-1">*/}
            {/*    <StandardsViewer parentId={benchSheetId} dataGridHeight="10rem"*/}
            {/*        technologyId={BenchSheetTechnologyType.GasChromatography} />*/}
            {/*</div>*/}

            {/*
            <div>
                <BatchStandardsComponentData batchId={batchId ? batchId : batchId}
                    isReadOnly={true} />

                <div>
                    <div className="lq-header pw-margin-top-05">
                        <div className="lq-header-title">Standards Documents</div>
                    </div>

                     Right now just have all standards documents listed under generic StandardsDocument type 
                    <DocumentLoader parentId={batchId} readOnly={true}
                        documentType={DocumentType.StandardsDocument}
                        canDelete={canDelete} />
                </div>               
            </div>
            */}

            <div className="pw-comp-row-scroll-fixed-grid">
                <NoteViewer height='100%' title="Bench Sheet Notes" readOnly={!canEdit}
                    noteType={NoteType.BenchSheet} parentId={benchSheetId}
                    headerColor='pw-bg-rb-600' />
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    hideOnOutsideClick={false}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="GC Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    // get functions

    async function getBenchSheet() {

        console.log("PARENT: getBenchSheet()");

        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then((data) => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    // show/hide

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showIssuesDialog() {
        //todo
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    // other functions

    async function getAllData() {
        await getBenchSheet();
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

